import Dialog from "@mui/material/Dialog";

import PlaybackOverlay, {
  PlaybackOverlayButton,
} from "@app/analytics/components/PlaybackOverlay";
import PlayerControls from "@app/analytics/components/PlayerControls";
import useHlsVideo from "@app/analytics/hooks/useHlsVideo";
import useVideoClip from "@app/analytics/hooks/useVideoClip";
import type { VideoClip } from "@app/analytics/types";
import type { VideoRecording } from "@app/common/types";

import {
  AbsolutePositioned,
  CloseButton,
  HtmlVideo,
  LoadingOverlay,
  VideoContainer,
} from "./styles";

interface VideoProps {
  id: string;
  clip: VideoClip;
  onClose: () => void;
  recordingInfo: VideoRecording;
}

interface VideoDialogProps extends Omit<VideoProps, "clip"> {
  clip: VideoClip | null;
  open: boolean;
}

const Video = ({ id, clip, onClose, recordingInfo }: VideoProps) => {
  const { video, videoRef, isLoading } = useHlsVideo(recordingInfo, {
    startTimestamp: clip?.start,
  });

  const {
    currentTime,
    controls,
    onActionClick,
    onScrubberChange,
    onVideoClick,
    played,
  } = useVideoClip(video, clip, { autoPlay: true, onMinimize: onClose });

  return (
    <VideoContainer>
      <HtmlVideo
        id={id}
        ref={videoRef}
        preload="auto"
        autoPlay
        onClick={onVideoClick}
      />
      {isLoading ? (
        <LoadingOverlay />
      ) : controls.paused ? (
        <PlaybackOverlay>
          <PlaybackOverlayButton
            icon={played ? "replay" : "play"}
            onClick={onVideoClick}
          />
          <PlaybackOverlayButton icon="close" onClick={onClose} />
        </PlaybackOverlay>
      ) : (
        <>
          <AbsolutePositioned top={8} right={9}>
            <CloseButton onClick={onClose} />
          </AbsolutePositioned>
          <AbsolutePositioned bottom={8} right={8} left={8}>
            <PlayerControls
              currentTime={currentTime}
              duration={clip.duration}
              muted={controls.muted}
              onActionClick={onActionClick}
              onScrubberChange={onScrubberChange}
              paused={controls.paused}
              expandable
              expanded
            />
          </AbsolutePositioned>
        </>
      )}
    </VideoContainer>
  );
};

const VideoDialog = ({
  id,
  clip,
  recordingInfo,
  open,
  onClose,
}: VideoDialogProps) => {
  if (!open || clip == null) return null;

  return (
    <Dialog open={open} onClose={onClose}>
      <Video
        id={id}
        clip={clip}
        recordingInfo={recordingInfo}
        onClose={onClose}
      />
    </Dialog>
  );
};

export default VideoDialog;
